import React, { useState } from 'react';
import { useQuery } from '@apollo/client';
import { Link } from 'react-router-dom';
import { Table } from 'antd';
import { TablePaginationConfig } from 'antd/lib/table';
// Api
import { GET_ADMIN_TICKET_ORDERS } from 'api/ticketing/queries';
// Types
import {
  GetAdminTicketOrders,
  GetAdminTicketOrdersVariables,
} from 'api/ticketing/types/GetAdminTicketOrders';
import { AdminTicketOrderRowFields } from 'api/ticketing/types/AdminTicketOrderRowFields';
import {
  SortDirection,
  TicketEventOrderSort,
  TicketOrderKind,
} from 'api/graphql-global-types';
// Helpers
import {
  formatEventDateTime,
  formatMoney,
  getTicketsLabel,
  pluralize,
} from 'pages/Events/helpers';
// Constants
import { TICKET_EVENT_DETAILS_GEN } from 'constants/routes';
// Components
import LoadErrorAlert from 'pages/Events/components/LoadErrorAlert/LoadErrorAlert';
import { OrderStatus } from 'pages/Events/components/OrderCells/OrderCells';

type TicketOrderRow = AdminTicketOrderRowFields;

type TicketsListProps = {
  customerId: string;
};

const TicketsList: React.FC<TicketsListProps> = ({ customerId }) => {
  const [pageSize, setPageSize] = useState<number>(10);
  const [currentPage, setCurrentPage] = useState<number>(1);

  const { data, loading, error, refetch } = useQuery<
    GetAdminTicketOrders,
    GetAdminTicketOrdersVariables
  >(GET_ADMIN_TICKET_ORDERS, {
    variables: {
      input: {
        buyerUserId: customerId,
        offset: (currentPage - 1) * pageSize,
        limit: pageSize,
        sortBy: TicketEventOrderSort.CreatedAt,
        direction: SortDirection.DESC,
      },
    },
    skip: !customerId,
    fetchPolicy: 'cache-and-network',
  });

  const orders = data?.adminTicketOrders.entities ?? [];

  const handleTableChange = (pagination: TablePaginationConfig): void => {
    setCurrentPage(pagination.current || 1);
    setPageSize(pagination.pageSize || 10);
  };

  if (error) {
    return (
      <LoadErrorAlert
        message="Failed to load event tickets"
        error={error}
        onRetry={refetch}
      />
    );
  }

  return (
    <Table<TicketOrderRow>
      rowKey="orderId"
      loading={loading}
      dataSource={orders}
      locale={{ emptyText: 'No event tickets yet.' }}
      onChange={handleTableChange}
      pagination={{
        current: currentPage,
        pageSize,
        total: data?.adminTicketOrders.total ?? 0,
        showSizeChanger: true,
      }}
      scroll={{ x: 960 }}
      columns={[
        {
          title: 'Event',
          key: 'event',
          render: (_, row) => (
            <Link to={`${TICKET_EVENT_DETAILS_GEN}/${row.ticketEvent.id}`}>
              {row.ticketEvent.name}
            </Link>
          ),
        },
        { title: 'Order', dataIndex: 'reference', key: 'reference' },
        {
          title: 'Tickets',
          key: 'tickets',
          render: (_, row) =>
            getTicketsLabel(row) || pluralize(row.admissionCount, 'ticket'),
        },
        {
          title: 'Paid',
          key: 'amount',
          render: (_, row) =>
            row.kind === TicketOrderKind.Comp
              ? 'Comp'
              : formatMoney(row.amount),
        },
        {
          title: 'Date',
          key: 'createdAt',
          render: (_, row) =>
            formatEventDateTime(row.createdAt, row.ticketEvent.timezone),
        },
        {
          title: 'Status',
          key: 'status',
          render: (_, row) => (
            <OrderStatus order={row} timezone={row.ticketEvent.timezone} />
          ),
        },
      ]}
    />
  );
};

export default TicketsList;
